import { fetchData } from 'Utils/fetch-data';
import type { FetchResponseI } from 'types';
import type { SessionsReportFiltersI, SessionsReportKpisI } from './sessions-report-api';
import type { SessionStatus, AttendanceStatus, SessionEvidenceI } from './sessions-api';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface EmployeeSessionRowI {
  id: string;
  startDateTime: string;
  endDateTime: string;
  durationHours: number;
  title: string;
  courseId: string;
  courseName: string;
  instructorName: string;
  status: SessionStatus;
  attendance: {
    status: AttendanceStatus | null;
    comment: string | null;
  };
  evidences: SessionEvidenceI[];
}

export interface EmployeeSessionsTotalsI {
  invited: number;
  present: number;
  justified: number;
  absent: number;
  pending: number;
  attendancePercentage: number;
  totalHours: number;
  lastSessionAt: string | null;
}

export interface EmployeeWithSessionsI {
  id: string;
  payrollNum: number | string;
  fullName: string;
  departmentName: string;
  jobTitleName: string;
  totals: EmployeeSessionsTotalsI;
  sessions: EmployeeSessionRowI[];
}

export interface SessionsEmployeesReportResponseI {
  kpis: SessionsReportKpisI;
  employees: EmployeeWithSessionsI[];
}

// ─────────────────────────────────────────────────────────────────────────────
// API client
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Aggregated sessions report grouped by employee.
 *
 * When the backend endpoint (`/reports/sessions/employees`) is not yet deployed,
 * `fetchData` returns {@link FetchResponseI.error} and the UI layer degrades
 * gracefully to an empty state — no runtime crash, no try/catch needed here.
 */
class SessionsEmployeesReportApi {
  private readonly endpoint = '/reports/sessions/employees';

  async fetchReport(filters: SessionsReportFiltersI = {}): Promise<FetchResponseI<SessionsEmployeesReportResponseI>> {
    return fetchData<SessionsEmployeesReportResponseI>(this.endpoint, {
      method: 'GET',
      params: filters,
      includePagination: true,
      includeJsonApiMeta: true,
    });
  }

  async exportReport(filters: SessionsReportFiltersI = {}): Promise<FetchResponseI<Blob>> {
    return fetchData<Blob>(`${this.endpoint}/export`, {
      method: 'GET',
      params: filters,
      responseType: 'blob',
    });
  }
}

const sessionsEmployeesReportApi = new SessionsEmployeesReportApi();
export default sessionsEmployeesReportApi;
